import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getSessionConfig, getRoleConfig } from '../config/auth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionInfo, setSessionInfo] = useState(null);

  const sessionConfig = getSessionConfig();

  useEffect(() => {
    // Check for existing authentication on app load
    const checkAuth = () => {
      try {
        const authData = localStorage.getItem(sessionConfig.storageKey);
        if (authData) {
          const { role, timestamp, sessionId, lastActivity } = JSON.parse(authData);
          
          // Check if session is not expired
          const now = Date.now();
          const sessionAge = now - timestamp;
          const activityAge = now - (lastActivity || timestamp);
          
          // Session expires after configured duration or 30 minutes of inactivity
          const maxInactivity = 30 * 60 * 1000; // 30 minutes
          
          if (sessionAge < sessionConfig.duration && activityAge < maxInactivity) {
            // Update last activity
            const updatedAuthData = {
              role,
              timestamp,
              sessionId,
              lastActivity: now
            };
            localStorage.setItem(sessionConfig.storageKey, JSON.stringify(updatedAuthData));
            
            setIsAuthenticated(true);
            setUserRole(role);
            setSessionInfo({
              sessionId,
              startTime: timestamp,
              lastActivity: now,
              expiresAt: timestamp + sessionConfig.duration
            });
          } else {
            // Session expired, clear it
            localStorage.removeItem(sessionConfig.storageKey);
            localStorage.removeItem('valeon_lockout'); // Clear any lockout data
          }
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        localStorage.removeItem(sessionConfig.storageKey);
        localStorage.removeItem('valeon_lockout');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();

    // Set up activity tracking
    const updateActivity = () => {
      if (isAuthenticated) {
        const authData = localStorage.getItem(sessionConfig.storageKey);
        if (authData) {
          try {
            const parsed = JSON.parse(authData);
            const updatedData = {
              ...parsed,
              lastActivity: Date.now()
            };
            localStorage.setItem(sessionConfig.storageKey, JSON.stringify(updatedData));
            
            setSessionInfo(prev => prev ? {
              ...prev,
              lastActivity: Date.now()
            } : null);
          } catch (error) {
            console.error('Error updating activity:', error);
          }
        }
      }
    };

    // Track user activity for session management
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    activityEvents.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
    };
  }, [isAuthenticated, sessionConfig]);

  // Define logout function using useCallback to avoid dependency issues
  const logout = useCallback((reason = 'manual') => {
    // Track logout event for analytics
    if (window.posthog && userRole) {
      window.posthog.capture('user_logout', {
        role: userRole,
        reason: reason,
        session_duration: sessionInfo ? Date.now() - sessionInfo.startTime : 0
      });
    }

    localStorage.removeItem(sessionConfig.storageKey);
    setIsAuthenticated(false);
    setUserRole(null);
    setSessionInfo(null);
  }, [userRole, sessionInfo, sessionConfig.storageKey]);

  // Auto-logout warning system
  useEffect(() => {
    if (!isAuthenticated || !sessionInfo) return;

    const checkSessionExpiry = () => {
      const now = Date.now();
      const timeUntilExpiry = sessionInfo.expiresAt - now;
      
      // Show warning 5 minutes before expiry
      if (timeUntilExpiry <= sessionConfig.warningTime && timeUntilExpiry > 0) {
        const minutes = Math.ceil(timeUntilExpiry / 60000);
        
        // Trigger session warning (could be implemented as toast notification)
        const isDev = (() => {
          try {
            return process?.env?.NODE_ENV === 'development';
          } catch {
            return false;
          }
        })();
        
        if (isDev) {
          console.warn(`Session expires in ${minutes} minute(s)`);
        }
        
        // TODO: Implement session warning toast notification
        // showSessionWarning(minutes);
      } else if (timeUntilExpiry <= 0) {
        // Session expired
        logout('Session expired');
      }
    };

    const interval = setInterval(checkSessionExpiry, 60000); // Check every minute
    
    return () => clearInterval(interval);
  }, [isAuthenticated, sessionInfo, sessionConfig.warningTime, logout]);

  const login = (role) => {
    const now = Date.now();
    const sessionId = crypto.randomUUID?.() || Math.random().toString(36);
    
    const authData = {
      role,
      timestamp: now,
      sessionId,
      lastActivity: now
    };
    
    localStorage.setItem(sessionConfig.storageKey, JSON.stringify(authData));
    
    setIsAuthenticated(true);
    setUserRole(role);
    setSessionInfo({
      sessionId,
      startTime: now,
      lastActivity: now,
      expiresAt: now + sessionConfig.duration
    });

    // Track login event for analytics
    if (window.posthog) {
      window.posthog.capture('user_login', {
        role: role,
        session_id: sessionId,
        timestamp: now
      });
    }

    // Navigate to homepage after successful login for all roles (v1.0.0 refinement)
    // Use setTimeout to ensure state updates complete before redirect
    setTimeout(() => {
      window.location.href = '/';
    }, 100);
  };

  const hasRole = (requiredRole) => {
    return userRole === requiredRole;
  };

  const hasAnyRole = (roles) => {
    return roles.includes(userRole);
  };

  const hasPermission = (permission) => {
    if (!userRole) return false;
    const roleConfig = getRoleConfig(userRole);
    return roleConfig?.permissions.includes(permission) || false;
  };

  const isFounder = () => userRole === 'founder';
  const isInvestor = () => userRole === 'investor';
  const isClient = () => userRole === 'client';

  const getSessionTimeRemaining = () => {
    if (!sessionInfo) return 0;
    const remaining = sessionInfo.expiresAt - Date.now();
    return Math.max(0, remaining);
  };

  const value = {
    isAuthenticated,
    userRole,
    isLoading,
    sessionInfo,
    login,
    logout,
    hasRole,
    hasAnyRole,
    hasPermission,
    isFounder,
    isInvestor,
    isClient,
    getSessionTimeRemaining
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;